import { Component, Input, OnInit } from '@angular/core';
import {
    FormControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { Options } from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { Subject } from 'rxjs';
import { ComponentOptionsHelper } from '../../helpers/componentOptions.helper';
import { IComponentOptionsDialogProperties } from '../../interfaces/IComponentOptionsDialogProperties.interface';
import { IComponentOptionsProperties } from '../../interfaces/IComponentOptionsProperties.interface';
import { IComponentOptionsDelegate } from './IComponentOptionsDelegate';

const debug = Debug('aumico:aumico-lib-notes-fe:ComponentOptionsComponent');

@Component({
    selector: 'aumico-notes-component-options',
    templateUrl: 'componentOptions.component.html',
    styleUrls: ['componentOptions.component.scss'],
})
export class ComponentOptionsComponent implements OnInit {
    @Input() formFields: IComponentOptionsProperties;
    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() translatable = true; // if options should have language tabs or not
    @Input() componentId: string;

    DELEGATE?: IComponentOptionsDelegate;
    @Input() public set delegate(delegate: IComponentOptionsDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    private deletedSelectionOptionsIndices: number[] = [];
    public removeSelectionOption: Subject<number[]> = new Subject();

    form: UntypedFormGroup;
    dynamicFormFieldsList: UntypedFormArray;
    hasToggle: boolean;

    staticFields: IComponentOptionsProperties = {
        properties: [],
    };
    dynamicFields: IComponentOptionsProperties = {
        properties: [],
    };

    constructor(private fb: UntypedFormBuilder, private dialogRef: MatDialogRef<ComponentOptionsComponent>) {
        dialogRef.disableClose = true;
    }

    ngOnInit(): void {
        debug('ngOnInit');
        this.initializeForm();
    }

    initializeForm() {
        this.separateStaticFromDynamicFormFields();
        this.form = this.fb.group({
            staticFields: this.createStaticFields(),
            dynamicFields: this.fb.array(this.getAllFormGroups()),
        });

        this.dynamicFormFieldsList = this.form.get('dynamicFields') as UntypedFormArray;
    }

    separateStaticFromDynamicFormFields() {
        this.formFields.properties.forEach((field) => {
            if (field.dynamic) {
                this.dynamicFields.properties.push(field);
            } else {
                this.staticFields.properties.push(field);
            }
        });
    }

    createStaticFields(): UntypedFormGroup {
        const formGroup = {};
        if (this.staticFields.properties.length) {
            this.staticFields.properties.forEach((formField: IComponentOptionsDialogProperties) => {
                // adding the condition !formField.toggle is due to the fact that a form might be translatable
                // yet has a toggle which is not translatable (like equity component)
                if (this.translatable && !formField.toggle) {
                    this.languages.forEach((language) => {
                        const formControlValue: string =
                            formField.value != null ? formField.value[language] : formField.value;
                        formGroup[formField.property[language]] = new UntypedFormControl(
                            formControlValue,
                            this.isFormFieldRequired(formField.required),
                        );
                    });
                } else {
                    this.hasToggle = formField.toggle ? true : false;
                    formGroup[formField.property as string] = new UntypedFormControl(
                        formField.value,
                        this.isFormFieldRequired(formField.required),
                    );
                }
            });
        }
        return this.fb.group(formGroup);
    }

    private getAllFormGroups(): UntypedFormGroup[] {
        const formGroups: UntypedFormGroup[] = [];
        if (this.dynamicFields.properties.length) {
            // TODO: make it to be general when needed
            const dynamicProperties = this.dynamicFields.properties[0];

            // Edit mode
            // if values are sent then loop on it and pass it to create form groups with values
            if (dynamicProperties.value != null && Array.isArray(dynamicProperties.value)) {
                (dynamicProperties.value as IDynamicKeyValue[]).forEach((element) => {
                    formGroups.push(this.createDynamicField(element));
                });
            } else {
                // Create mode
                formGroups.push(this.createDynamicField());
            }
        }

        return formGroups;
    }

    createDynamicField(data?: object): UntypedFormGroup {
        const formGroup = {};

        if (this.dynamicFields.properties.length) {
            const dynamicProperties = this.dynamicFields.properties[0];

            this.languages.forEach((language) => {
                const formControlValue: string = data != null ? data[language] : null;
                formGroup[dynamicProperties.property[language]] = new UntypedFormControl(
                    formControlValue,
                    this.isFormFieldRequired(dynamicProperties.required),
                );
            });
        }

        return this.fb.group(formGroup);
    }

    private isFormFieldRequired(required: boolean) {
        if (!required) {
            return null;
        }

        return Validators.required;
    }

    addItem() {
        this.dynamicFormFieldsList.push(this.createDynamicField());
    }

    remove(index: number, removedField: FormControl) {
        this.dynamicFormFieldsList = this.form.get('dynamicFields') as UntypedFormArray;
        const dynamicPropertiesValue = this.dynamicFields.properties[0].value as IDynamicKeyValue[];

        const removedObjectIndex = dynamicPropertiesValue?.findIndex(
            (value) => value['de-ch'] === removedField.value['selectionOption-de-ch'],
        );
        if (removedObjectIndex != null && removedObjectIndex >= EMPTY_ARRAY_LENGTH) {
            this.deletedSelectionOptionsIndices.push(removedObjectIndex);
        }

        let uniqueDeletedSelectionOptionsIndices = [...new Set(this.deletedSelectionOptionsIndices)];
        this.removeSelectionOption.next(uniqueDeletedSelectionOptionsIndices);

        this.dynamicFormFieldsList.removeAt(index);
    }

    submit() {
        if (!this.form.valid) {
            return;
        }
        if (this.DELEGATE) {
            debug('component options');
            debug(this.form.value);
            // map each form control to its language and combine static and dynamic fields
            if (this.translatable) {
                const mappedOutput = ComponentOptionsHelper.mapFormValueTranslationsAndCombineFields(
                    this.formFields,
                    this.staticFields,
                    this.dynamicFields,
                    this.form,
                );
                debug('mappedOutput', mappedOutput);
                this.DELEGATE.savedOptions(mappedOutput as Options, this.componentId);
            } else {
                // Currently this is the case of totalRow toggle button
                debug('output', this.form.value.staticFields);
                this.DELEGATE.savedOptions(this.form.value.staticFields as Options, this.componentId);
            }

            this.dialogRef.close();
        }
    }

    closeDialog() {
        this.dialogRef.close('empty');
    }

    getMandatoryText(): string {
        return 'aumico_core_form_mandatory_error';
    }
}
